import { useState } from "react";
import { Twitter, Linkedin, Facebook, Mail, Link2, Check, Share2 } from "lucide-react";

/**
 * ShareBar — accessible, dependency-free social-sharing row.
 *
 * Uses share intents (open a new tab to the network's share endpoint) instead
 * of third-party JS SDKs. Also offers the native Web Share API on mobile when
 * available, and a copy-link fallback with a "copied" toast state.
 *
 * Props:
 *   title       — page/article title (used in tweet and mailto subject)
 *   description — short excerpt (mailto body, twitter text prefix)
 *   url         — absolute URL to share (defaults to window.location.href)
 *   variant     — "compact" (icons only) | "labeled" (icons + tiny labels)
 *   className   — extra tailwind classes on the wrapper
 */
export default function ShareBar({
  title = "",
  description = "",
  url,
  variant = "labeled",
  className = "",
}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = (url && url.startsWith("http") ? url : (typeof window !== "undefined" ? window.location.href : url)) || "";
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDesc = encodeURIComponent(description);

  const links = [
    {
      key: "twitter",
      label: "X / Twitter",
      icon: <Twitter size={14} />,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      color: "hover:bg-black hover:text-white",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: <Linkedin size={14} />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "hover:bg-[#0a66c2] hover:text-white",
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: <Facebook size={14} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "hover:bg-[#1877f2] hover:text-white",
    },
    {
      key: "email",
      label: "Email",
      icon: <Mail size={14} />,
      href: `mailto:?subject=${encodedTitle}&body=${encodedDesc}%0A%0A${encodedUrl}`,
      color: "hover:bg-[var(--navy)] hover:text-white",
      internal: true, // no target=_blank for mailto
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers: fall back to a select-and-execCommand pattern
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2000); }
      finally { document.body.removeChild(ta); }
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: description, url: shareUrl }); }
      catch { /* user cancelled */ }
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="share-bar" aria-label="Share this page">
      <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--muted)] font-bold mr-1">Share</span>
      {links.map((l) => (
        <a
          key={l.key}
          href={l.href}
          target={l.internal ? "_self" : "_blank"}
          rel="noreferrer"
          className={`inline-flex items-center gap-1.5 border border-[var(--line)] text-[var(--navy)] px-2.5 py-1.5 text-[11px] transition-colors ${l.color}`}
          data-testid={`share-${l.key}`}
          aria-label={`Share on ${l.label}`}
        >
          {l.icon}
          {variant === "labeled" && <span>{l.label}</span>}
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[11px] transition-colors ${
          copied
            ? "border-emerald-600 text-emerald-700 bg-emerald-50"
            : "border-[var(--line)] text-[var(--navy)] hover:bg-[var(--cream)]"
        }`}
        data-testid="share-copy"
        aria-label="Copy link to clipboard"
      >
        {copied ? <><Check size={14} /> {variant === "labeled" && "Copied"}</> : <><Link2 size={14} /> {variant === "labeled" && "Copy link"}</>}
      </button>
      {canNativeShare && (
        <button
          type="button"
          onClick={nativeShare}
          className="inline-flex items-center gap-1.5 border border-[var(--line)] text-[var(--navy)] px-2.5 py-1.5 text-[11px] hover:bg-[var(--cream)] md:hidden"
          data-testid="share-native"
          aria-label="Open native share sheet"
        >
          <Share2 size={14} />
        </button>
      )}
    </div>
  );
}
